/* eslint-disable react-native/no-inline-styles */
/**
 * The app navigator (formerly "AppNavigator" and "MainNavigator") is used for the primary
 * navigation flows of your app.
 * Generally speaking, it will contain an auth flow (registration, login, forgot password)
 * and a "main" flow which the user will use once logged in.
 */
import { NavigationContainer } from "@react-navigation/native"
import { createNativeStackNavigator, NativeStackScreenProps } from "@react-navigation/native-stack"
import { observer } from "mobx-react-lite"
import React from "react"
import { useColorScheme, View, StyleSheet } from "react-native"
import Config from "../config"
import { navigationRef, useBackButtonHandler } from "./navigationUtilities"
import { colors, typography } from "app/theme"
import FlashMessage from "react-native-flash-message"
import { AuthStack } from "./AuthStack"

import { MainStack } from "./MainStack"
import { useStores } from "models"
import { EmployeeStack } from "./EmployeeStack"
import { black } from "react-native-paper/lib/typescript/styles/themes/v2/colors"
import { TabNavigator } from "./BottomTabNavigator"
import { useColor } from "app/theme/getThemes"

/**
 * This type allows TypeScript to know what routes are defined in this navigator
 * as well as what properties (if any) they might take when navigating to them.
 *
 * If no params are allowed, pass through `undefined`. Generally speaking, we
 * recommend using your MobX-State-Tree store(s) to keep application state
 * rather than passing state through navigation params.
 *
 * For more information, see this documentation:
 *   https://reactnavigation.org/docs/params/
 *   https://reactnavigation.org/docs/typescript#type-checking-the-navigator
 *   https://reactnavigation.org/docs/typescript/#organizing-types
 */
export type AppStackParamList = {
  auth: undefined
  app: undefined
  employee: undefined
}

/**
 * This is a list of all the route names that will exit the app if the back button
 * is pressed while in that screen. Only affects Android.
 */
const exitRoutes = Config.exitRoutes

export type AppStackScreenProps<T extends keyof AppStackParamList> = NativeStackScreenProps<
  AppStackParamList,
  T
>

// Documentation: https://reactnavigation.org/docs/stack-navigator/
const Stack = createNativeStackNavigator<AppStackParamList>()

// const AppStack = observer(function AppStack() {
//   const { userStore } = useStores()
//   return (
//     <Stack.Navigator
//       initialRouteName={
//         userStore?.isLoggedIn ? (userStore?.role === "employee" ? "employee" : "app") : "auth"
//       }
//       screenOptions={{
//         headerShown: true,
//         headerTitleAlign: "center",
//         headerTitleStyle: {
//           color: "white",
//         },
//         headerTintColor: "white",
//       }}
//     >
//       <Stack.Screen name="auth" component={AuthStack} options={{ headerShown: false }} />
//       <Stack.Screen name="app" component={MainStack} options={{ headerShown: false }} />
//       <Stack.Screen name="employee" component={EmployeeStack} options={{ headerShown: false }} />
//     </Stack.Navigator>
//   )
// })

const AppStack = observer(function AppStack() {
  const { userStore } = useStores()
  const { companyTheme } = useColor()

  return (
    <Stack.Navigator
      initialRouteName={
        userStore?.isLoggedIn ? (userStore?.role === "employee" ? "employee" : "app") : "auth"
      }
      screenOptions={{
        headerShown: true,
        headerStyle: {
          backgroundColor: companyTheme.first,
        },
        headerTitleAlign: "center",
        headerTitleStyle: {
          color: "white",
        },
        headerTintColor: "white",
      }}
    >
      <Stack.Screen name="auth" component={AuthStack} options={{ headerShown: false }} />
      <Stack.Screen name="app" component={TabNavigator} options={{ headerShown: false }} />
      <Stack.Screen name="employee" component={EmployeeStack} options={{ headerShown: false }} />
    </Stack.Navigator>
  )
})

export interface NavigationProps
  extends Partial<React.ComponentProps<typeof NavigationContainer>> {}

export const AppNavigator = observer(function AppNavigator(props: NavigationProps) {
  const colorScheme = useColorScheme()

  useBackButtonHandler((routeName) => exitRoutes.includes(routeName))

  return (
    <View style={styles.fullContainer}>
      <View style={styles.container}>
        <NavigationContainer ref={navigationRef} {...props}>
          <AppStack />
          <FlashMessage
            titleStyle={{
              fontSize: 17,
              textAlign: "center",
              fontFamily: typography.primary.normal,
            }}
          />
        </NavigationContainer>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    alignSelf: "center",
    flex: 1,
    maxWidth: colors.appMaxWidth,
    width: "100%",
  },
  fullContainer: {
    backgroundColor: colors.mainBgColor,
    flex: 1,
    width: "100%",
  },
})
